import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import stripTrailingPeriod from 'utils/stripTrailingPeriod';
import Metrics from '../TNOMediaKit/components/Metrics';
import TnoEcosystemSection from './components/TnoEcosystemSection';

/** Claves de icono de propuesta → nombres que entiende `TNOMediaKit/components/Metrics` */
const MEDIAKIT_METRIC_ICON_MAP = {
  store: 'LiveTvIcon',
  digest: 'LanguageIcon',
  reels: 'InstagramIcon',
  event: 'OndemandVideoIcon',
  mic: 'YouTubeIcon',
};

const ClientProposalLayout = ({ content, themeMode, proposalName }) => {
  const theme = useTheme();
  const hero = content.hero || {};
  const tnoEcosystem = content.tnoEcosystem;
  const productsExplained = content.productsExplained || {};
  const explainedItems = Array.isArray(productsExplained.items) ? productsExplained.items : [];
  const explainedItemsFullRow = productsExplained.itemsLayout === 'fullRow';
  const products = Array.isArray(content.products) ? content.products : [];
  const productsLayout = content.productsLayout || 'cards';
  const metrics = Array.isArray(content.metrics) ? content.metrics : [];
  const metricsSection = content.metricsSection || {};
  const bonificacion = content.bonificacion || {};
  const valueAdd = content.valueAdd || {};
  const investmentSummary = content.investmentSummary || {};
  const summaryItems = Array.isArray(investmentSummary.items) ? investmentSummary.items : [];
  const shouldShowInvestmentColumn = summaryItems.some(
    (row) => row?.investment && row.investment.toLowerCase() !== 'incluido'
  );
  const hasMonetaryPrices =
    shouldShowInvestmentColumn
    || products.some(
      (item) => item?.investment && String(item.investment).toLowerCase() !== 'incluido',
    )
    || Boolean(hero.investmentNote);
  const areYouReady = content.areYouReady || {};
  const whatsappRaw = areYouReady.whatsappNumber || '584241234567';
  const whatsappNumber = String(whatsappRaw).replace(/\D/g, '');

  const renderValueAddIcon = (iconName) => {
    const baseProps = { width: 28, height: 28, xmlns: 'http://www.w3.org/2000/svg', fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' };
    switch (iconName) {
      case 'handshake':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11l3 3a2 2 0 002.828 0L17 11m-9 5l-3 3m12-3l3 3M3 9l4-4a2 2 0 012.828 0L12 7l2.172-2.172a2 2 0 012.828 0L21 9" />
          </svg>
        );
      case 'chart':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 14l3-3 3 2 4-5" />
          </svg>
        );
      case 'wallet':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2m0-6h4v6h-4a2 2 0 010-4h4" />
          </svg>
        );
      case 'network':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5V4h-5M2 20h5V10H2m10 10h5V14h-5m0-10h5V4h-5M2 4h5v2H2z" />
          </svg>
        );
      case 'shield':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
          </svg>
        );
      default:
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
    }
  };

  const renderProductIcon = (iconName) => {
    const baseProps = { width: 34, height: 34, xmlns: 'http://www.w3.org/2000/svg', fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor' };
    switch (iconName) {
      case 'mic':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 1v11m0 0a4 4 0 004-4V5a4 4 0 00-8 0v3a4 4 0 004 4zm-7 0a7 7 0 0014 0m-7 7v4m-4 0h8" />
          </svg>
        );
      case 'event':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'megaphone':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5l7 4v6l-7 4V5zM4 10v4a2 2 0 002 2h1l2 4h2l-2-4h2" />
          </svg>
        );
      default:
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
    }
  };

  const renderExplainedIcon = (iconName) => {
    const baseProps = {
      width: 28,
      height: 28,
      xmlns: 'http://www.w3.org/2000/svg',
      fill: 'none',
      viewBox: '0 0 24 24',
      stroke: 'currentColor',
    };
    switch (iconName) {
      case 'ugc':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14m-9 3h6a2 2 0 002-2V9a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
          </svg>
        );
      case 'seo':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 18a8 8 0 100-16 8 8 0 000 16zm0 0l7 3" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10h6M7 13h4M7 7h6" />
          </svg>
        );
      case 'web':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h18v14H3V5zm0 4h18M8 3v4" />
          </svg>
        );
      case 'store':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l2-5h14l2 5M5 9v10h14V9M9 19v-6h6v6" />
          </svg>
        );
      case 'broadcast':
        return (
          <svg {...baseProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5v14M4 6a11 11 0 000 12M20 6a11 11 0 010 12" />
          </svg>
        );
      default:
        return renderProductIcon(iconName);
    }
  };

  const renderTopicCheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );

  const renderExplainedFullRowItem = (item, index) => {
    const accent = theme.palette.primary.main;
    const step = String(index + 1).padStart(2, '0');

    return (
      <Box
        component="article"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: alpha(accent, 0.14),
          boxShadow: `0 1px 0 ${alpha(accent, 0.06)}`,
          transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
          '&:hover': {
            borderColor: alpha(accent, 0.28),
            boxShadow: `0 16px 48px ${alpha(accent, 0.1)}`,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: 2,
            minWidth: { md: 88 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: 2,
              color: alpha(accent, 0.55),
              lineHeight: 1,
            }}
          >
            {step}
          </Typography>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accent, 0.12),
              color: 'primary.main',
            }}
          >
            {renderExplainedIcon(item.icon)}
          </Box>
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1.25}>
            <Chip
              label={item.badge || 'Plan mensual'}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: alpha(accent, 0.1),
                color: 'primary.dark',
                border: `1px solid ${alpha(accent, 0.2)}`,
              }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.25, mb: 1.25, color: 'text.primary' }}>
            {item.title}
          </Typography>
          {item.summary && (
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.75,
                color: 'text.secondary',
                maxWidth: 920,
                mb: Array.isArray(item.topics) && item.topics.length > 0 ? 2.5 : 0,
              }}
            >
              {item.summary}
            </Typography>
          )}
          {Array.isArray(item.topics) && item.topics.length > 0 && (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }}
              gap={1.5}
            >
              {item.topics.map((topic, j) => (
                <Box
                  key={j}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : alpha(accent, 0.04),
                    border: '1px solid',
                    borderColor: alpha(accent, 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      flexShrink: 0,
                      mt: 0.1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(accent, 0.14),
                      color: 'primary.main',
                    }}
                  >
                    {renderTopicCheckIcon()}
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.55, color: 'text.primary', fontWeight: 500 }}>
                    {topic}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Container>
        <Box marginBottom={4}>
          <Container
            sx={{
              backgroundImage:
                themeMode === 'dark'
                  ? 'url(/images/logos/logo_white.PNG)'
                  : 'url(/images/logos/logo_tnonetwork_black.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              height: '16vh',
            }}
          />
          <Typography variant="overline" sx={{ textTransform: 'uppercase', fontWeight: 600 }} color="textSecondary" align="center" display="block" gutterBottom>
            {hero.overline || 'Propuesta Comercial'}
          </Typography>
          <Box component={Typography} fontWeight={700} variant="h3" gutterBottom align="center" color={theme.palette.primary.main}>
            {stripTrailingPeriod(hero.title || proposalName)}
          </Box>
          {hero.subtitle && (
            <Typography variant="h6" component="p" color="textSecondary" align="center" sx={{ fontStyle: 'italic', mb: 3 }}>
              {hero.subtitle}
            </Typography>
          )}
          {hero.image && (
            <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 4 }}>
              <Box
                component="img"
                src={hero.image}
                alt={hero.imageAlt || proposalName || 'Logo'}
                sx={{
                  width: '100%',
                  maxWidth: 240,
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
          {(hero.analysisTitle || hero.analysisText) && (
            <Box sx={{ maxWidth: 720, mx: 'auto', py: 2, px: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50' }}>
              {hero.analysisTitle && (
                <Typography variant="h6" component="p" color="textSecondary" align="center" gutterBottom>
                  <strong>{hero.analysisTitle}</strong>
                </Typography>
              )}
              {hero.analysisText && (
                <Typography variant="body1" color="textSecondary" align="center">
                  {hero.analysisText}
                </Typography>
              )}
            </Box>
          )}
          {hero.investmentNote && (
            <Box sx={{ maxWidth: 560, mx: 'auto', mt: 3, py: 1.5, px: 2, borderRadius: 2, border: `2px solid ${theme.palette.primary.main}`, bgcolor: theme.palette.primary.main + '14' }}>
              <Typography variant="body1" align="center" fontWeight={600} color="primary">
                {hero.investmentNote}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      {tnoEcosystem && <TnoEcosystemSection section={tnoEcosystem} />}

      {explainedItems.length > 0 && (
        <Box sx={{ bgcolor: explainedItemsFullRow ? (theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50') : 'transparent' }}>
          <Container>
            <Box marginBottom={4} py={explainedItemsFullRow ? { xs: 4, md: 6 } : 0}>
              {explainedItemsFullRow ? (
                <>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1.2 }} display="block" gutterBottom>
                    {productsExplained.overline || 'Ejecución'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 720 }}>
                    {stripTrailingPeriod(productsExplained.title || 'Detalle de Productos')}
                  </Typography>
                  {productsExplained.description && (
                    <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.85, maxWidth: 900, mb: 4 }}>
                      {productsExplained.description}
                    </Typography>
                  )}
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    {explainedItems.map((item, i) => (
                      <Box key={item.id || i}>{renderExplainedFullRowItem(item, i)}</Box>
                    ))}
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
                    {productsExplained.overline || 'Ejecucion'}
                  </Typography>
                  <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
                    {stripTrailingPeriod(productsExplained.title || 'Detalle de Productos')}
                  </Box>
                  {productsExplained.description && (
                    <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 860, mx: 'auto', mb: 4 }}>
                      {productsExplained.description}
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    {explainedItems.map((item, i) => (
                      <Grid item xs={12} md={6} key={item.id || i}>
                        <Card
                          elevation={3}
                          sx={{
                            height: '100%',
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              gap: 1,
                              background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
                              <Box
                                component={Avatar}
                                variant="rounded"
                                sx={{
                                  width: 46,
                                  height: 46,
                                  flexShrink: 0,
                                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                                  color: 'primary.main',
                                }}
                              >
                                {renderExplainedIcon(item.icon)}
                              </Box>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                {item.title}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                              {item.badge || 'Plan mensual'}
                            </Typography>
                          </Box>
                          {item.illustration && (
                            <Box
                              component="img"
                              src={item.illustration}
                              alt={item.illustrationAlt || item.title}
                              sx={{
                                width: '100%',
                                height: 140,
                                objectFit: 'cover',
                              }}
                            />
                          )}
                          <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                              {item.title}
                            </Typography>
                            {item.summary && (
                              <Typography variant="body1" color="textSecondary" paragraph sx={{ lineHeight: 1.65 }}>
                                {item.summary}
                              </Typography>
                            )}
                            {Array.isArray(item.topics) && item.topics.length > 0 && (
                              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                                {item.topics.map((topic, j) => (
                                  <li key={j}>
                                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.55 }}>
                                      {topic}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          </Container>
        </Box>
      )}

      {products.length > 0 && (
        <Container>
          <Box marginBottom={4}>
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
              Productos
            </Typography>
            <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
              Propuesta por bloques
            </Box>
          </Box>
          {productsLayout === 'whatWeDo' ? (
            <Grid container spacing={4}>
              {products.map((item, i) => (
                <Grid item xs={12} sm={6} md={4} key={item.id || i}>
                  <Box display="flex" flexDirection="column" data-aos="fade-up">
                    <Box
                      component={Avatar}
                      variant="rounded"
                      width={72}
                      height={72}
                      marginBottom={2}
                      bgcolor={alpha(theme.palette.primary.main, 0.1)}
                      borderRadius={3}
                    >
                      <Box color={theme.palette.primary.main}>
                        {renderProductIcon(item.icon)}
                      </Box>
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {item.title}
                    </Typography>
                    {item.illustration && (
                      <Box
                        component="img"
                        src={item.illustration}
                        alt={item.illustrationAlt || item.title}
                        sx={{
                          width: '100%',
                          maxWidth: 220,
                          height: 'auto',
                          mb: 1.5,
                        }}
                      />
                    )}
                    {item.product && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {item.product}
                      </Typography>
                    )}
                    {Array.isArray(item.impacts) && item.impacts.length > 0 && (
                      <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                        {item.impacts.map((impact, j) => (
                          <li key={j}>
                            <Typography variant="body2" color="textSecondary">{impact}</Typography>
                          </li>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={4}>
              {products.map((item, i) => (
                <Grid item xs={12} md={4} key={item.id || i}>
                  <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {item.image && (
                      <Box component="img" src={item.image} alt={item.imageAlt || ''} sx={{ width: '100%', height: 200, objectFit: 'cover', objectPosition: item.objectPosition || 'center' }} />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" color="primary" gutterBottom>{item.title}</Typography>
                      {item.product && <Typography variant="body2" color="textSecondary" gutterBottom>{item.product}</Typography>}
                      {item.investment && <Typography fontWeight={600}>{item.investment}</Typography>}
                      {Array.isArray(item.impacts) && item.impacts.length > 0 && (
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          {item.impacts.map((impact, j) => (
                            <li key={j}><Typography variant="body2" color="textSecondary">{impact}</Typography></li>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      )}

      {metrics.length > 0 && (
        <Container>
          <Metrics
            metrics={metrics.map((m) => ({
              ...m,
              icon: MEDIAKIT_METRIC_ICON_MAP[m.icon] || m.icon || 'LanguageIcon',
            }))}
            content={{
              overline: metricsSection.overline,
              title: metricsSection.title,
              description: metricsSection.description,
            }}
            hideAudienceImage
          />
        </Container>
      )}

      {(bonificacion.title || (Array.isArray(bonificacion.items) && bonificacion.items.length > 0)) && (
        <Container>
          <Box marginBottom={4}>
            {bonificacion.title && <Typography variant="h5" color="primary" align="center" gutterBottom>{stripTrailingPeriod(bonificacion.title)}</Typography>}
            {Array.isArray(bonificacion.items) && (
              <Box component="ul" sx={{ maxWidth: 720, mx: 'auto' }}>
                {bonificacion.items.map((item, i) => (
                  <li key={i}><Typography variant="body1">{typeof item === 'string' ? item : item.text}</Typography></li>
                ))}
              </Box>
            )}
          </Box>
        </Container>
      )}

      {(valueAdd.title || (Array.isArray(valueAdd.items) && valueAdd.items.length > 0)) && (
        <Container>
          <Box marginBottom={4}>
            {valueAdd.layout === 'features' ? (
              <>
                <Box marginBottom={4}>
                  <Typography
                    sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
                    gutterBottom
                    color="secondary"
                    align="center"
                  >
                    {valueAdd.overline || 'Features'}
                  </Typography>
                  {valueAdd.title && (
                    <Box
                      component={Typography}
                      fontWeight={700}
                      variant="h3"
                      align="center"
                      gutterBottom
                    >
                      {valueAdd.title}
                    </Box>
                  )}
                </Box>
                {Array.isArray(valueAdd.items) && valueAdd.items.length > 0 && (
                  <Grid container spacing={4}>
                    {valueAdd.items.map((item, i) => {
                      const rawText = typeof item === 'string' ? item : item.text;
                      const [titlePart, ...restParts] = String(rawText).split(':');
                      const subtitle = typeof item === 'object' && item.description
                        ? item.description
                        : restParts.length > 0
                          ? restParts.join(':').trim()
                          : String(rawText);
                      const title = typeof item === 'object' && item.title
                        ? item.title
                        : restParts.length > 0
                          ? titlePart.trim()
                          : `Diferencial ${i + 1}`;
                      const iconName = typeof item === 'object' ? item.icon : undefined;
                      return (
                        <Grid key={i} item xs={12} md={6}>
                          <ListItem
                            component="div"
                            disableGutters
                            sx={{ alignItems: 'flex-start', padding: 0 }}
                          >
                            <ListItemAvatar>
                              <Box color={theme.palette.primary.main}>
                                {renderValueAddIcon(iconName)}
                              </Box>
                            </ListItemAvatar>
                            <ListItemText
                              primary={title}
                              secondary={subtitle}
                              primaryTypographyProps={{
                                variant: 'h6',
                                gutterBottom: true,
                                sx: { fontWeight: 700 },
                              }}
                              sx={{ margin: 0 }}
                            />
                          </ListItem>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </>
            ) : (
              <>
                {valueAdd.title && <Typography variant="h5" color="primary" align="center" gutterBottom>{stripTrailingPeriod(valueAdd.title)}</Typography>}
                {Array.isArray(valueAdd.items) && valueAdd.items.map((item, i) => (
                  <Typography key={i} variant="body1" paragraph>{typeof item === 'string' ? item : item.text}</Typography>
                ))}
              </>
            )}
          </Box>
        </Container>
      )}

      {summaryItems.length > 0 && (
        <Container>
          <Box marginBottom={4}>
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
              Resumen
            </Typography>
            <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
              Resumen de Inversión
            </Box>
          </Box>
          <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 720, mx: 'auto', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Producto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Detalle</TableCell>
                  {shouldShowInvestmentColumn && (
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Inversión</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryItems.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.product}</TableCell>
                    <TableCell>{row.detail}</TableCell>
                    {shouldShowInvestmentColumn && (
                      <TableCell align="right">{row.investment}</TableCell>
                    )}
                  </TableRow>
                ))}
                {investmentSummary.totalLabel && (
                  <TableRow sx={{ bgcolor: theme.palette.primary.light }}>
                    <TableCell
                      colSpan={shouldShowInvestmentColumn ? 2 : 1}
                      sx={{ fontWeight: 700, color: 'common.white' }}
                    >
                      {investmentSummary.totalLabel}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'common.white' }}>
                      {investmentSummary.totalValue}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {investmentSummary.totalNote && (
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2, maxWidth: 720, mx: 'auto' }}>
              {investmentSummary.totalNote}
            </Typography>
          )}
        </Container>
      )}

      {hasMonetaryPrices && (
        <Container>
          <PriceVatDisclaimer />
        </Container>
      )}

      {(areYouReady.title || areYouReady.description || areYouReady.footerText) && (
        <Container>
          <Box marginBottom={4} textAlign="center">
            {areYouReady.title && <Typography variant="h4" color="primary" gutterBottom>{areYouReady.title}</Typography>}
            {areYouReady.description && <Typography variant="body1" color="textSecondary" paragraph>{areYouReady.description}</Typography>}
            {areYouReady.ctaText && (
              <Button component="a" href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" color="primary" variant="contained" size="large" sx={{ mt: 2 }}>
                {areYouReady.ctaText}
              </Button>
            )}
            {areYouReady.footerText && <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>{areYouReady.footerText}</Typography>}
          </Box>
        </Container>
      )}
    </>
  );
};

ClientProposalLayout.propTypes = {
  content: PropTypes.object.isRequired,
  themeMode: PropTypes.string,
  proposalName: PropTypes.string,
};

export default ClientProposalLayout;
